import { BUSINESS_TYPES, type BusinessType } from '@acs/shared';
import { Field, Select } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';

/**
 * What kind of business this is (doc 23) — open while setup runs, then fixed.
 *
 * Fixed once setup is finished, and the reason is what the field is FOR: it
 * is the segmentation key ("which verticals actually convert and retain"), and
 * a value owners can flip on a working business turns that history into
 * noise. An owner whose business genuinely changed shape later archives it and
 * starts a new one — which is the honest move anyway, since the services,
 * hours and client base underneath were built for the old shape.
 *
 * Until then it is a correction like any other: the creation screen asks it
 * first, and the wizard's first step shows it again, editable, so a wrong pick
 * costs nothing before the assistant has ever answered a call. In Settings it
 * is always read-only, with the archive route spelled out rather than left for
 * the owner to guess. `firestore.rules` enforces the same rule server-side, so
 * this is a control that tells the truth rather than one that hides a
 * rejection.
 *
 * Nothing in the product branches on the value today: no prompt insert, no
 * gating, no different onboarding for a dental practice. That stays true until
 * the segmentation data says which vertical is worth a fork (doc 23 §7.2).
 */
export function BusinessTypeField({
  value,
  onChange,
  locked = false,
}: {
  /** Null until answered — which is also every business created before doc 23. */
  value: BusinessType | null;
  onChange: (v: BusinessType) => void;
  /** Read-only: setup is finished, or a screen that may never change it. */
  locked?: boolean;
}) {
  const { t } = useI18n();

  return (
    <Field
      label={t('businessType.label')}
      required
      disabled={locked}
      hint={
        !locked
          ? t('businessType.hint')
          : // Read-only AND unanswered: a business created before this field
            // existed. Telling that owner it was "fixed at creation" would be
            // false — the wizard's first step is where they can still answer.
            value === null
            ? t('businessType.notSetYet')
            : t('businessType.locked')
      }
    >
      <Select
        value={value ?? ''}
        disabled={locked}
        onChange={(e) => onChange(e.target.value as BusinessType)}
      >
        {/* Present only while unanswered: an empty option that can be chosen
            again would let an owner un-answer a question we treat as settled. */}
        {value === null && <option value="">{t('businessType.placeholder')}</option>}
        {BUSINESS_TYPES.map((id) => (
          <option key={id} value={id}>
            {t(`businessType.${id}`)}
          </option>
        ))}
      </Select>
    </Field>
  );
}
